import { useState } from 'react'
import { MdEmail, MdExpandMore } from 'react-icons/md';
import { format } from 'date-fns';
import { BsFillTelephoneFill } from 'react-icons/bs';
import useDelete from '../../hook/useDelete';
import { delete_FamilyMember } from '../../services/FamilyMember';
import { delete_BulkFamilyMemberSubGroupByFamilyMemberId } from '../../services/FamilMemberSubGroup';
import useAuth from '../../hook/useAuth';
import baseURL from '../../config/api';
import { formatUSPhone } from '../../utils/phoneFormatter';
import Cookies from 'js-cookie';

export enum CurrentPage {
  ADD_FAMILY,
  ADD,
  EDIT
};

type Props = {
  setCurrentPage: (value: CurrentPage) => void;
  familyMember: any[];
  setFamilyMember: (value: any[]) => void;
  setSelectedRowIndex: (value: number) => void;
  fromEdit?: boolean;
}

const AdminFamilyMemberList = ({ setCurrentPage, familyMember, setFamilyMember, setSelectedRowIndex, fromEdit = false }: Props) => {
  const token = Cookies.get("token-my-cdir");
  const { auth }: any = useAuth();
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const { setDeletePopup, setData: setDeleteData }: any = useDelete();

  const addFamilyMember = () => {
    setCurrentPage(CurrentPage.ADD)
  }

  const editFamilyMember = (index: number) => {
    setCurrentPage(CurrentPage.EDIT)
    setSelectedRowIndex(index);
  }

  const deleteFamilyMember = (id: string, index: number) => {
    if (fromEdit && parseInt(id) > 0) {
      setDeletePopup(true);
      setDeleteData({
        id: id,
        tilte: `Delete`,
        summary: `Are you sure you want to delete this member?`,
        isDelete: (callback: any) => {
          delete_BulkFamilyMemberSubGroupByFamilyMemberId(id, auth?.id, token!.toString());
          delete_FamilyMember(id, token!.toString())
            .then(() => {
              setFamilyMember(familyMember.filter((item: any) => item?.id !== parseInt(id)));
              setDeletePopup(false);
              callback();
            })
            .catch(error => console.error(error));
        }
      });
    } else {
      setDeletePopup(true);
      setDeleteData({
        id: 0,
        tilte: `Delete`,
        summary: `Are you sure you want to delete this family member?`,
        isDelete: (callback: any) => {
          setFamilyMember(familyMember.filter((_: any, ind) => ind !== index))
          setDeletePopup(false);
          callback();
        }
      });
    }
  }

  return (
    <section className="user__container mt-2">
      <div
        className="min-w-full flex flex-col xm:flex-row justify-between items-start xm:items-center gap-3 text-xs xm:text-sm md:text-base"
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
      >
        <h1 className="px-2 xm:px-4 py-1.5 flex items-center gap-2">
          <MdExpandMore
            className={`text-2xl transform duration-300 ${isExpanded ? "rotate-0" : "-rotate-90"
              }`}
          />
          <span className="xm:text-sm md:text-base lg:text-lg">
            Member
          </span>
        </h1>

        <button
          className="bg-secondary hover:bg-custom-blue px-4 py-2 xm:py-1.5 rounded-sm text-white w-full xm:w-auto"
          onClick={addFamilyMember}
        >
          Add Member
        </button>
      </div>

      <div className="min-w-full h-0.5 bg-slate-500 my-2" />

      {isExpanded && (
        <>
          {familyMember.length > 0 ? (
            <div className="member__list__container grid grid-cols-1 xm:grid-cols-2 gap-y-4 gap-x-4 m-auto w-full">
              {familyMember.map((item, index) => (
                <div
                  key={index}
                  className="member__view__container flex flex-col w-full h-fit gap-3 rounded-lg py-3 px-2 bg-gray-50 shadow-2xl"
                >
                  <div className="flex flex-col xm:flex-row gap-3 px-2">
                    {/* IMAGE */}
                    <img
                      src={`${baseURL}commonFile/Get?name=${item.filePath}`}
                      alt="family-member"
                      className="w-full xm:w-[150px] xm:h-[150px] object-cover object-top"
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = "/images/user-default.jpg";
                      }}
                    />

                    {/* TEXT */}
                    <div className="text__container flex flex-col flex-1 justify-center items-center xm:items-start w-full px-2 text-center xm:text-left">
                      <h1 className="name__text text-base xm:text-lg font-bold break-words">
                        {item.firstName + " " + item.lastName}
                      </h1>
                      <h3 className="name__text text-xs xm:text-sm font-semibold">
                        {item?.birthday
                          ? format(new Date(item?.birthday), "MM/dd/yyyy")
                          : ""}
                      </h3>
                    </div>

                    {/* ACTION + INFO */}
                    <div className="family__view flex flex-col-reverse xm:flex-col min-w-[150px] w-full">
                      <div className="flex flex-col xm:flex-row gap-2 w-full">
                        <button
                          className="bg-secondary hover:bg-custom-blue px-4 py-2 xm:py-1 rounded-sm font-bold text-white w-full"
                          onClick={() => editFamilyMember(index)}
                        >
                          Edit
                        </button>

                        <button
                          className="bg-secondary hover:bg-custom-blue px-4 py-2 xm:py-1 rounded-sm font-bold text-white w-full"
                          onClick={() => deleteFamilyMember(item.id, index)}
                        >
                          Delete
                        </button>
                      </div>

                      <div className="text-black flex flex-col items-center xm:items-start mt-3 xm:mt-4">
                        <div className="flex flex-row justify-between xm:justify-start items-center gap-3 py-1 w-full">
                          <BsFillTelephoneFill />
                          <p className="flex-1 text-sm xm:text-base font-semibold">
                            {formatUSPhone(item.phoneNumber || "")}
                          </p>
                        </div>

                        <div className="flex flex-row items-center gap-2 py-1 w-full">
                          <MdEmail size={15} />
                          <p className="flex-1 text-left text-sm xm:text-base font-semibold break-all">
                            {item.emailAddress}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="bg-blue-100 text-blue-800 pl-4 pr-4 xm:pr-10 py-4 rounded-md relative mt-4 md:mt-8 text-sm xm:text-base"
              role="alert"
            >
              <strong className="font-bold">Info!</strong>
              <span className="block xm:inline mt-1 xm:mt-0 mx-0 xm:mx-4">
                This is a info message that you need to add at least one family member
              </span>
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default AdminFamilyMemberList;